import styled from "styled-components/macro";
import { Button } from "../Button";
import SVG from "react-inlinesvg";
import { useAppStore } from "../../stores/appStore";
import React, { useRef, useEffect } from "react";
import { useCommentStore } from "../../stores/commentStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { getSudowriteTabCompletion } from "./replyBoxTabCompletion";
import { getReplyBoxHeightPx, getReplyBoxHintPosition, REPLY_BOX_MIN_HEIGHT_PX } from "./replyBoxLayout";

export const ReplyBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  width: 100%;

  form {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .comment-card-input {
    width: 100%;
    box-sizing: border-box;
    padding: 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    font-family: "Inter", sans-serif;
    font-size: 16px;
    line-height: 23px;
    text-align: left;
    resize: none;
    overflow: hidden;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    word-break: break-word;
    min-height: ${REPLY_BOX_MIN_HEIGHT_PX}px;
    height: ${REPLY_BOX_MIN_HEIGHT_PX}px;
    transition: height 0.2s ease-in-out;
    background-color: var(--menu-color-fullscreen);
    color: var(--grey-0);
    &::placeholder {
      color: var(--text-basic-tertiary, var(--grey-2));
    }

    &:focus {
      outline: none;
      border-color: var(--primary-cta-high-contrast);
    }
  }

  .comment-card-input.mirror-input {
    position: absolute;
    inset: 0;
    z-index: 0;
    visibility: hidden;
    pointer-events: none;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    word-break: break-word;
    color: transparent;
    background: transparent;
    border-color: transparent;
    transition: none;
  }
`;

export const ReplyActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
`;

const ReplyFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const TextareaShell = styled.div`
  position: relative;
`;

const InlineTabCompletionHint = styled.div`
  position: absolute;
  z-index: 2;
  display: inline;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--grey-2);
  font-size: ${(props) => (props.isMobile ? "16px" : "14px")};
  line-height: 1.5;
  pointer-events: none;
  white-space: pre-wrap;
`;

const CompletionHintText = styled.span`
  display: inline;
  color: var(--grey-2);
  line-height: 1.5;
  white-space: inherit;
  overflow-wrap: break-word;
  word-break: normal;
`;

const Keycap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  top: -2px;
  min-width: 36px;
  height: 24px;
  padding: 0 3px;
  margin-left: 8px;
  border-radius: 8px;
  border: 1px solid var(--grey-3);
  background: color-mix(in srgb, var(--grey-3) 45%, transparent);
  color: var(--grey-1);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  text-transform: lowercase;
  vertical-align: middle;
`;

export default function ReplyBox({
  submitComment,
  cancelComment,
  newComment,
  setNewComment,
  newCommentInputBoxRef,
  placeholder,
  buttonText,
  alwaysShowActionButtons = false,
  isMobile = false,
  isSubmitting = false,
}) {
  const isMac = useAppStore((state) => state.isMac);
  const isSharedDocument = useCommentStore((state) => state.isSharedDocument);
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat ?? state.subscription?.hasAccessToAgenticChat ?? false);
  const enableSudowriteSupport = !isSharedDocument && hasAccessToAgenticChat;

  const [isInputFocused, setIsInputFocused] = React.useState(false);
  const [isActionFocused, setIsActionFocused] = React.useState(false);
  const [inputHeightPx, setInputHeightPx] = React.useState(REPLY_BOX_MIN_HEIGHT_PX);
  const [selection, setSelection] = React.useState({ start: 0, end: 0 });
  const [hintPosition, setHintPosition] = React.useState({ top: 0, left: 0, maxWidth: 0 });
  const setActiveAnnotationIds = useCommentStore((state) => state.setActiveAnnotationIds);

  const blurTimeoutRef = useRef(null);
  const inputElRef = useRef(null);
  const mirrorElRef = useRef(null);
  const caretMarkerRef = useRef(null);
  const hintElRef = useRef(null);
  const pendingSelectionRef = useRef(null);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setIsInputFocused(false), 300);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    setIsInputFocused(false);
    setIsActionFocused(false);
    inputElRef.current?.blur();
    cancelComment();
    if (!isSubmitting) {
      setActiveAnnotationIds([]);
    }
  };

  const cancelFromKeyboard = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    setIsInputFocused(false);
    setIsActionFocused(false);
    inputElRef.current?.blur();
    cancelComment();
  };

  const handleActionFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    setIsActionFocused(true);
  };

  const handleActionBlur = (e) => {
    const nextFocusTarget = e.relatedTarget;
    const actionContainer = e.currentTarget.closest("[data-reply-actions]");
    if (actionContainer && nextFocusTarget instanceof Node && actionContainer.contains(nextFocusTarget)) {
      return;
    }
    setIsActionFocused(false);
  };

  const updateSelectionFromTarget = (target) => {
    const nextStart = target.selectionStart ?? 0;
    const nextEnd = target.selectionEnd ?? 0;
    setSelection((prev) => {
      if (prev.start === nextStart && prev.end === nextEnd) {
        return prev;
      }
      return { start: nextStart, end: nextEnd };
    });
  };

  const resizeInputToContent = (target) => {
    if (!target) return;
    target.style.height = "auto";
    const nextHeight = getReplyBoxHeightPx(target.scrollHeight, REPLY_BOX_MIN_HEIGHT_PX);
    target.style.height = `${nextHeight}px`;
    setInputHeightPx(nextHeight);
  };

  const tabCompletion = enableSudowriteSupport ? getSudowriteTabCompletion(newComment, selection.start, selection.end) : null;
  const showTabCompletionHint = isInputFocused && Boolean(tabCompletion);
  const hintText = tabCompletion?.hintText ?? "";
  const beforeCaretText = newComment.slice(0, selection.start);

  useEffect(() => {
    if (!showTabCompletionHint) return;
    const inputEl = inputElRef.current;
    const caretMarkerEl = caretMarkerRef.current;
    const hintEl = hintElRef.current;
    if (!inputEl || !caretMarkerEl || !hintEl) return;

    const updateHintPosition = () => {
      const inputRect = inputEl.getBoundingClientRect();
      const markerRect = caretMarkerEl.getBoundingClientRect();
      const hintRect = hintEl.getBoundingClientRect();
      const computedInputStyle = window.getComputedStyle(inputEl);
      const lineHeightPx = Number.parseFloat(computedInputStyle.lineHeight) || markerRect.height;
      const paddingLeftPx = Number.parseFloat(computedInputStyle.paddingLeft) || 0;
      const paddingRightPx = Number.parseFloat(computedInputStyle.paddingRight) || 0;
      const inputContentWidthPx = Math.max(0, inputEl.clientWidth - paddingLeftPx - paddingRightPx);

      const {
        top: nextTop,
        left: nextLeft,
        maxWidth: nextMaxWidth,
      } = getReplyBoxHintPosition({
        markerTopPx: markerRect.top,
        inputTopPx: inputRect.top,
        markerHeightPx: markerRect.height,
        hintHeightPx: hintRect.height,
        lineHeightPx,
        paddingLeftPx,
        inputContentWidthPx,
        hintWidthPx: hintRect.width,
        markerLeftPx: markerRect.left,
        inputLeftPx: inputRect.left,
        scrollTopPx: inputEl.scrollTop,
      });
      setHintPosition((prev) => {
        if (prev.top === nextTop && prev.left === nextLeft && prev.maxWidth === nextMaxWidth) {
          return prev;
        }
        return { top: nextTop, left: nextLeft, maxWidth: nextMaxWidth };
      });
    };

    const raf = requestAnimationFrame(updateHintPosition);
    return () => cancelAnimationFrame(raf);
  }, [showTabCompletionHint, beforeCaretText, selection.start, selection.end, newComment]);

  useEffect(() => {
    resizeInputToContent(inputElRef.current);
  }, [newComment]);

  React.useLayoutEffect(() => {
    if (!isInputFocused || !inputElRef.current) return;
    if (pendingSelectionRef.current) {
      const { start, end } = pendingSelectionRef.current;
      inputElRef.current.setSelectionRange(start, end);
      pendingSelectionRef.current = null;
    }
    updateSelectionFromTarget(inputElRef.current);
  }, [newComment, isInputFocused]);

  return (
    <ReplyBoxWrapper
      className="reply-box"
      isMobile={isMobile}
    >
      <form onSubmit={submitComment}>
        <TextareaShell>
          <textarea
            className="comment-card-input"
            autoComplete="off"
            placeholder={showTabCompletionHint && !newComment ? "" : placeholder}
            ref={(el) => {
              inputElRef.current = el;
              if (newCommentInputBoxRef) {
                if (typeof newCommentInputBoxRef === "function") {
                  newCommentInputBoxRef(el);
                } else {
                  newCommentInputBoxRef.current = el;
                }
              }
            }}
            onFocus={() => {
              setIsInputFocused(true);
              if (inputElRef.current) {
                updateSelectionFromTarget(inputElRef.current);
              }
            }}
            onBlur={handleBlur}
            onClick={(e) => updateSelectionFromTarget(e.currentTarget)}
            onKeyUp={(e) => updateSelectionFromTarget(e.currentTarget)}
            onSelect={(e) => updateSelectionFromTarget(e.currentTarget)}
            onScroll={(e) => updateSelectionFromTarget(e.currentTarget)}
            value={newComment}
            onChange={(e) => {
              pendingSelectionRef.current = {
                start: e.target.selectionStart,
                end: e.target.selectionEnd,
              };
              setNewComment(e.target.value);
              updateSelectionFromTarget(e.target);
              resizeInputToContent(e.target);
            }}
            onKeyDown={(e) => {
              if (enableSudowriteSupport && e.key === "Tab" && !e.shiftKey) {
                const inputEl = e.currentTarget;
                const completion = getSudowriteTabCompletion(newComment, inputEl.selectionStart ?? 0, inputEl.selectionEnd ?? 0);
                if (completion) {
                  e.preventDefault();
                  setNewComment(completion.value);
                  requestAnimationFrame(() => {
                    inputEl.focus();
                    resizeInputToContent(inputEl);
                    inputEl.setSelectionRange(completion.caretPosition, completion.caretPosition);
                  });
                  return;
                }
              }

              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                if (isSubmitting) {
                  return;
                }
                submitComment(e);
              }

              if (e.key === "Escape") {
                cancelFromKeyboard();
              }
            }}
          />
          <div
            ref={mirrorElRef}
            className="comment-card-input mirror-input"
            aria-hidden="true"
            style={{ height: `${inputHeightPx}px` }}
          >
            {beforeCaretText.length > 0 ? beforeCaretText : " "}
            <span ref={caretMarkerRef}>|</span>
          </div>
          {showTabCompletionHint && (
            <InlineTabCompletionHint
              ref={hintElRef}
              isMobile={isMobile}
              style={{
                top: `${hintPosition.top - 2}px`,
                left: `${hintPosition.left}px`,
                maxWidth: `${hintPosition.maxWidth}px`,
              }}
            >
              {hintText ? <CompletionHintText>{hintText}</CompletionHintText> : null}
              <Keycap>tab</Keycap>
            </InlineTabCompletionHint>
          )}
        </TextareaShell>
        {(isInputFocused || isActionFocused || alwaysShowActionButtons) && (
          <ReplyFooter>
            <ReplyActions data-reply-actions>
              <Button
                className="use-component-styles"
                small
                type="button"
                onClick={handleCancel}
                onFocus={handleActionFocus}
                onBlur={handleActionBlur}
              >
                Cancel
              </Button>
              <Button
                className="use-component-styles"
                primary
                type="submit"
                disabled={isSubmitting}
                onFocus={handleActionFocus}
                onBlur={handleActionBlur}
              >
                {buttonText}

                {!isMobile && (
                  <>
                    <SVG
                      src={isMac ? "/assets/keyboard-shortcuts/command-key.svg" : "/assets/keyboard-shortcuts/control-key-pc.svg"}
                      aria-hidden="true"
                      height={19}
                      width={21}
                      style={{
                        marginLeft: "5px",
                        border: "2px solid rgba(255, 255, 255, 0.4)",
                        borderRadius: "5px",
                      }}
                    />
                    <SVG
                      src="/assets/keyboard-shortcuts/enter-key.svg"
                      aria-hidden="true"
                      height={19}
                      width={21}
                      style={{
                        border: "2px solid rgba(255, 255, 255, 0.4)",
                        borderRadius: "5px",
                      }}
                    />
                  </>
                )}
              </Button>
            </ReplyActions>
          </ReplyFooter>
        )}
      </form>
    </ReplyBoxWrapper>
  );
}
